import classNames from 'clsx';
import video2x from 'img/quota-landing/beautifulAddress/1080 x 480.webm';
import React, { type ReactElement, type ReactNode, memo, useCallback, useEffect, useState } from 'react';
import { Button, ButtonSizeMode } from 'reactApp/ui/Button/Button';
import Content from 'reactApp/ui/Content/Content';
import { CopyWeblink } from 'reactApp/ui/CopyWeblink/CopyWeblink';
import { type DialogSize, Dialog } from 'reactApp/ui/Dialog/Dialog';
import { noop } from 'reactApp/utils/helpers';
import opener from 'reactApp/utils/opener';

import styles from './WhatsNewDialog.css';

export interface IProps {
    className?: string;
    titleClassName?: string;
    textClassName?: string;
    imageWrapperClassName?: string;
    imageClassName?: string;
    contentClassName?: string;
    closeIconClassName?: string;
    closeIconWrapperClassName?: string;
    footerClassName?: string;
    primaryButtonText?: ReactNode;
    primaryButtonClassName?: string;
    primaryButtonHref?: string;
    primaryButtonTargetBlank?: boolean;
    secondaryButtonText?: string;
    secondaryButtonClassName?: string;
    secondaryButtonHref?: string;
    secondaryButtonTargetBlank?: boolean;
    date?: number;
    title: string | ReactElement;
    text?: string | ReactElement;
    content?: ReactElement;
    bulletedContent?: { icon: ReactElement; text: ReactNode }[];
    bulletItemClassName?: string;
    hideImage?: boolean;
    imageUrl: string;
    imageBgColor?: string;
    imageFluid?: boolean;
    closeBySecondaryButton?: boolean;
    closeAfterClick?: boolean;
    closable?: boolean;
    link?: string;
    footerText?: ReactElement;
    primaryButtonColor?: 'black' | 'grey';
    secondaryButtonColor?: 'black' | 'grey';
    qaId?: string;
    dialogSize?: DialogSize;
    footerTextBelowButtons?: boolean;
    footerTextRightToButtons?: boolean;
    buttonTheme?: 'vk' | 'octavius';
    isRebranding?: boolean;
    isRebrandingQuotaLanding?: boolean;
    imageAdditionalContent?: ReactElement;
    onPrimaryButtonHover?: () => void;
    onSecondaryButtonHover?: () => void;
    actionButtonText?: string;
    isPrimaryDisabled?: boolean;
    isSecondaryDisabled?: boolean;
    closeOnEscape?: boolean;
    preventStackDimmer?: boolean;
    bigCloseIcon?: boolean;
    onShow?(): void;

    // любое закрытие
    onClose?(): void;

    // закрытие не по экшену
    onDismiss?(): void;

    onClick?(): void;

    onSecondary?(): void;

    onActionClick?(): void;
    imageOnlyAdditionalContent?: boolean;
    showVideo?: boolean;
    loopVideo?: boolean;
    videoUrl?: { webm?: string; mp4?: string };
    videoWrapperClass?: string;
    videoClass?: string;
    closeOnDimmerClick?: boolean;
    showDetails?: boolean;
    detailsText?: string | ReactElement;
}

// eslint-disable-next-line max-lines-per-function
export const WhatsNewDialog = memo(function WhatsNewDialog({
    className,
    titleClassName,
    textClassName,
    imageWrapperClassName,
    imageClassName,
    contentClassName,
    closeIconClassName,
    closeIconWrapperClassName,
    footerClassName,
    primaryButtonText,
    primaryButtonClassName,
    secondaryButtonHref,
    primaryButtonHref,
    primaryButtonTargetBlank = false,
    secondaryButtonTargetBlank = false,
    onClick = noop,
    onSecondary = noop,
    onClose = noop,
    onDismiss = noop,
    onShow = noop,
    secondaryButtonText,
    date = 0,
    title,
    text,
    content,
    bulletedContent,
    bulletItemClassName,
    link,
    closable = true,
    imageUrl,
    imageBgColor,
    imageFluid = true,
    closeAfterClick = false,
    closeBySecondaryButton = false,
    closeOnEscape,
    preventStackDimmer = false,
    actionButtonText,
    onActionClick = noop,
    footerText,
    primaryButtonColor,
    secondaryButtonColor,
    qaId,
    dialogSize = 'large',
    footerTextBelowButtons = false,
    footerTextRightToButtons = false,
    buttonTheme = 'octavius',
    isRebranding = false,
    isRebrandingQuotaLanding = false,
    imageAdditionalContent,
    hideImage = false,
    onPrimaryButtonHover,
    onSecondaryButtonHover,
    isPrimaryDisabled = false,
    isSecondaryDisabled = false,
    imageOnlyAdditionalContent = false,
    showVideo,
    videoUrl,
    videoWrapperClass,
    videoClass,
    loopVideo = false,
    bigCloseIcon = true,
    closeOnDimmerClick = false,
    secondaryButtonClassName,
    showDetails = false,
    detailsText,
}: IProps): ReactElement {
    const [open, setOpen] = useState(true);
    useEffect(() => onShow(), []);

    const onPrimaryHandler = useCallback(() => {
        onClick();
        if (closeAfterClick) {
            setOpen(false);
        }
        if (primaryButtonHref) {
            opener(primaryButtonHref, !primaryButtonTargetBlank);
        }
    }, [onClick, primaryButtonHref, closeAfterClick, primaryButtonTargetBlank]);

    const onSecondaryHandler = useCallback(() => {
        onSecondary();

        if (closeAfterClick) {
            setOpen(false);
        }

        if (secondaryButtonHref) {
            opener(secondaryButtonHref, !secondaryButtonTargetBlank);
        }

        if (closeBySecondaryButton) {
            onClose?.();
        }
    }, [onSecondary, secondaryButtonHref, onClose, closeBySecondaryButton, closeAfterClick, secondaryButtonTargetBlank]);

    const onCloseHandler = useCallback(() => {
        onDismiss?.();
        onClose?.();
    }, [onClose, onDismiss]);

    useEffect(() => {
        if (!open) {
            onClose?.();
        }
    }, [
        /** onClose */
        open,
    ]);

    return (
        <Dialog
            open={open}
            header={''}
            closeOnDimmerClick={closeOnDimmerClick}
            closeOnEscape={closeOnEscape}
            id="whatsnew-dlg"
            qaId={qaId}
            onCancel={onCloseHandler}
            size={dialogSize}
            bigCloseIcon={bigCloseIcon}
            closable={closable}
            className={classNames(styles.root, className, {
                [styles[`root_${dialogSize}`]]: dialogSize,
            })}
            closeIconWrapperClassName={closeIconWrapperClassName}
            closeIconClassName={closeIconClassName}
            isRebrandingQuotaLanding={isRebrandingQuotaLanding}
            preventStackDimmer={preventStackDimmer}
        >
            <Content isModal>
                <div
                    className={classNames(styles.content, contentClassName, {
                        [styles.content_rebrandingQuotaLanding]: isRebrandingQuotaLanding,
                        [styles.normalTopPadding]: hideImage,
                    })}
                >
                    {!hideImage ? (
                        <div
                            className={classNames(styles.imageWrapper, imageWrapperClassName)}
                            style={{
                                backgroundColor: imageBgColor,
                            }}
                        >
                            {!imageOnlyAdditionalContent && (
                                <img
                                    className={classNames(styles.image, imageClassName, {
                                        [styles.image_fluid]: imageFluid,
                                    })}
                                    src={imageUrl}
                                    alt="Cover image"
                                    data-qa-id="image"
                                />
                            )}
                            {imageAdditionalContent}
                        </div>
                    ) : null}
                    {showVideo ? (
                        <div className={classNames(styles.videoWrapper, videoWrapperClass)}>
                            <video loop={loopVideo} autoPlay muted playsInline className={classNames(styles.video, videoClass)}>
                                <source src={videoUrl?.webm || video2x} type="video/webm" />
                                {videoUrl?.mp4 && <source src={videoUrl?.mp4} type="video/mp4" />}
                            </video>
                        </div>
                    ) : null}
                    {Boolean(date) && <div className={styles.date}>{date}</div>}
                    <div
                        data-qa-id="title"
                        className={classNames(styles.title, titleClassName, { [styles.titleWithHiddenImage]: hideImage })}
                    >
                        {title}
                    </div>
                    {text && (
                        <div className={classNames(styles.text, textClassName)}>
                            {typeof text === 'string' ? text.replace(/\\n/g, '\n') : text}
                        </div>
                    )}
                    {Boolean(content) && content}
                    {Boolean(bulletedContent) && (
                        <div className={styles.bulletsWrapper}>
                            {bulletedContent?.map(({ icon, text }, idx) => (
                                <div key={idx} className={classNames(styles.bulletItem, bulletItemClassName)}>
                                    {icon}
                                    {text}
                                </div>
                            ))}
                        </div>
                    )}
                    <div
                        className={classNames(styles.footerWrapper, footerClassName, {
                            [styles.footerWrapperColumn]: footerTextBelowButtons,
                            [styles.footerWrapperRowReverse]: footerTextRightToButtons,
                        })}
                    >
                        {footerText}
                        <div className={styles.buttonWrapper}>
                            {actionButtonText && (
                                <Button
                                    className={classNames({
                                        [styles[`button_${buttonTheme}`]]: !!buttonTheme,
                                    })}
                                    data-name="freeze-renewal"
                                    theme={buttonTheme}
                                    primary
                                    sizeMode={buttonTheme === 'vk' ? ButtonSizeMode.small : ButtonSizeMode.middle}
                                    onClick={onActionClick}
                                >
                                    {actionButtonText}
                                </Button>
                            )}
                            {primaryButtonText && (
                                <Button
                                    className={classNames(
                                        {
                                            [styles[`button_${buttonTheme}`]]: !!buttonTheme,
                                            [styles[`button_${primaryButtonColor}`]]: !!primaryButtonColor,
                                        },
                                        primaryButtonClassName
                                    )}
                                    onClick={onPrimaryHandler}
                                    data-name="success"
                                    theme={buttonTheme}
                                    disabled={isPrimaryDisabled}
                                    primary={!actionButtonText}
                                    secondary={Boolean(actionButtonText)}
                                    sizeMode={buttonTheme === 'vk' ? ButtonSizeMode.small : ButtonSizeMode.middle}
                                    onPointerEnter={onPrimaryButtonHover}
                                >
                                    {primaryButtonText}
                                </Button>
                            )}
                            {secondaryButtonText && (
                                <Button
                                    className={classNames(
                                        {
                                            [styles[`button_${buttonTheme}`]]: !!buttonTheme,
                                            [styles[`button_${secondaryButtonColor}`]]: !!secondaryButtonColor,
                                            [styles.button_rebranding]: isRebranding,
                                            [styles.secondaryButton_rebrandingQuotaLanding]: isRebrandingQuotaLanding,
                                        },
                                        secondaryButtonClassName
                                    )}
                                    onClick={onSecondaryHandler}
                                    theme={buttonTheme}
                                    disabled={isSecondaryDisabled}
                                    data-name="secondary"
                                    sizeMode={buttonTheme === 'vk' ? ButtonSizeMode.small : ButtonSizeMode.middle}
                                    onPointerEnter={onSecondaryButtonHover}
                                >
                                    {secondaryButtonText}
                                </Button>
                            )}
                            {link && (
                                <div className={styles.copyWeblink}>
                                    <CopyWeblink publicUrl={link} type="new" onCopy={onPrimaryHandler} />
                                </div>
                            )}
                        </div>
                    </div>
                    {showDetails && (
                        <div className={styles.afterFooterContainer}>
                            <span className={styles.details}>{detailsText}</span>
                        </div>
                    )}
                </div>
            </Content>
        </Dialog>
    );
});
